/**
 * MobX store for the Duplicate Store troubleshooting page: lists store copy attempts
 * (with per-action-type counters) and triggers new duplications. Kept separate from the shared
 * TroubleshootingStore so the attempts list is decoupled from the generic shopifyResponse.
 */
import { makeAutoObservable, runInAction } from 'mobx';

import { rootContainer, TYPE } from '../../../../services/container/root';
import TroubleshootingService, {
    IStoreCopyAttempt,
    IStoreCopyAttemptsFilters,
    IPagination,
    StaleEntityMode,
} from '../../../../services/api/troubleshooting-service';

class DuplicateStorePageStore {
    attempts: IStoreCopyAttempt[] = [];
    pagination: IPagination | null = null;
    isLoading: boolean = false;
    error: string | null = null;

    private get troubleshootingService(): TroubleshootingService {
        return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
    }

    constructor() {
        makeAutoObservable(this);
    }

    async fetchAttempts(filters: IStoreCopyAttemptsFilters = {}): Promise<void> {
        runInAction(() => {
            this.isLoading = true;
            this.error = null;
        });
        try {
            const response = await this.troubleshootingService.getStoreCopyAttempts(filters);
            runInAction(() => {
                this.attempts = response?.data ?? [];
                this.pagination = response?.pagination ?? null;
                this.isLoading = false;
            });
        } catch (err: any) {
            runInAction(() => {
                this.error = err?.response?.data?.error ?? err?.message ?? 'Failed to load attempts';
                this.isLoading = false;
            });
        }
    }

    /**
     * Dispatches a new duplicate-store run. Returns the raw API response so the page can
     * surface the dispatch outcome (pending/failed). Does not mutate the attempts list;
     * the caller should re-fetch afterwards.
     */
    async triggerDuplicate(
        toStoreId: string | number,
        fromStoreIdOrName: string | number,
        staleEntityMode?: StaleEntityMode,
    ): Promise<any> {
        return this.troubleshootingService.duplicateStore(toStoreId, fromStoreIdOrName, staleEntityMode);
    }
}

/** TODO: find a way to instantiate store by demand */
export default new DuplicateStorePageStore();
